import { Plus, Trash } from "@phosphor-icons/react";
import { Button } from "./ui/button";

const inputCls = "h-11 w-full rounded-xl border border-stone-300 bg-white px-4 text-base focus:border-[#6A3E97] focus:outline-none";
const areaCls = "min-h-28 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-base focus:border-[#6A3E97] focus:outline-none";

export function masterApplies(q, answers) {
  const cond = q.conditional || q.show_if;
  if (!cond) return true;
  const raw0 = answers?.[cond.key];
  const raw = raw0 && typeof raw0 === "object" && !Array.isArray(raw0) && "value" in raw0 ? raw0.value : raw0;
  const vals = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? Object.values(raw) : [raw];
  const clean = vals.map((v) => String(v || ""));
  if (cond.in) return clean.some((v) => cond.in.includes(v));
  if (cond.not_in) return clean.some(Boolean) && !clean.some((v) => cond.not_in.includes(v));
  return true;
}

function ChoiceButtons({ value, choices, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {(choices || []).filter(Boolean).map((choice) => {
        const on = value === choice;
        return (
          <button key={choice} type="button" onClick={() => onChange(choice)}
            className={`rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${on ? "border-[#4A235A] bg-[#4A235A] text-white" : "border-stone-300 bg-white text-stone-700 hover:border-[#4A235A]"}`}
            data-testid={`${testid}-${choice.replace(/\W+/g, "-").toLowerCase()}`}>
            {choice}
          </button>
        );
      })}
    </div>
  );
}

function CheckboxButtons({ value, choices, onChange, testid }) {
  const arr = Array.isArray(value) ? value : [];
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {(choices || []).map((choice) => {
        const on = arr.includes(choice);
        return (
          <button key={choice} type="button"
            onClick={() => onChange(on ? arr.filter((x) => x !== choice) : [...arr, choice])}
            className={`rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${on ? "border-[#4A235A] bg-[#4A235A] text-white" : "border-stone-300 bg-white text-stone-700 hover:border-[#4A235A]"}`}
            data-testid={`${testid}-${choice.replace(/\W+/g, "-").toLowerCase()}`}>
            {on ? "✓ " : ""}{choice}
          </button>
        );
      })}
    </div>
  );
}

function SubField({ spec, value, onChange, testid }) {
  const body = spec.type === "textarea" ? (
    <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className={areaCls} data-testid={testid} />
  ) : spec.type === "checkboxes" ? (
    <CheckboxButtons value={value} choices={spec.choices} onChange={onChange} testid={testid} />
  ) : spec.type === "choice" || spec.type === "yesno" ? (
    <ChoiceButtons value={value || ""} choices={spec.choices} onChange={onChange} testid={testid} />
  ) : (
    <input type={spec.type === "date" ? "date" : "text"} value={value || ""} onChange={(e) => onChange(e.target.value)}
      className={inputCls} data-testid={testid} />
  );
  return (
    <div className="rounded-xl border border-stone-200 bg-stone-50/70 p-4">
      <p className="mb-2 text-sm font-semibold text-stone-700">{spec.label}</p>
      {body}
    </div>
  );
}

function RepeatRows({ q, value, onChange, testid }) {
  const rows = Array.isArray(value) ? value : [];
  const setRow = (idx, suffix, v) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, [suffix]: v } : r));
    onChange(next);
  };
  return (
    <div className="space-y-4" data-testid={testid}>
      {rows.map((row, idx) => (
        <div key={idx} className="rounded-2xl border border-stone-200 bg-white p-4" data-testid={`${testid}-row-${idx}`}>
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-bold text-stone-800">Entry {idx + 1}</p>
            <button type="button" onClick={() => onChange(rows.filter((_, i) => i !== idx))}
              className="inline-flex items-center gap-1 text-xs font-semibold text-stone-400 hover:text-[#C0392B]"
              data-testid={`${testid}-remove-${idx}`}>
              <Trash size={13} /> Remove
            </button>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            {(q.subparts || []).map((spec) => (
              <div key={spec.suffix} className={spec.type === "textarea" || spec.type === "checkboxes" ? "md:col-span-2" : ""}>
                <SubField spec={spec} value={row[spec.suffix]} onChange={(v) => setRow(idx, spec.suffix, v)}
                  testid={`${testid}-${idx}-${spec.suffix}`} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" onClick={() => onChange([...rows, {}])}
        className="rounded-full" data-testid={`${testid}-add`}>
        <Plus size={15} className="mr-1" /> Add another
      </Button>
    </div>
  );
}

export default function MasterIntakeQuestion({ q, value, onChange, testidPrefix = "mi" }) {
  const testid = `${testidPrefix}-${q.key}`;
  const hasSubparts = (q.subparts || []).length > 0 && q.type !== "group" && q.type !== "repeat";
  const packed = hasSubparts ? (value && typeof value === "object" && !Array.isArray(value) ? value : {}) : value;
  const baseValue = hasSubparts ? packed.value : value;
  const setBase = (v) => onChange(hasSubparts ? { ...packed, value: v } : v);
  let body;
  if (q.type === "textarea") {
    body = <textarea value={baseValue || ""} onChange={(e) => setBase(e.target.value)} className={areaCls} data-testid={testid} autoFocus />;
  } else if (q.type === "choice" || q.type === "yesno") {
    body = <ChoiceButtons value={baseValue || ""} choices={q.choices || q.options} onChange={setBase} testid={testid} />;
  } else if (q.type === "checkboxes") {
    body = <CheckboxButtons value={baseValue} choices={q.choices || q.options} onChange={setBase} testid={testid} />;
  } else if (q.type === "group") {
    const group = value && typeof value === "object" && !Array.isArray(value) ? value : {};
    body = (
      <div className="grid gap-3 md:grid-cols-2" data-testid={testid}>
        {(q.subparts || []).map((spec) => (
          <div key={spec.suffix} className={spec.type === "textarea" || spec.type === "checkboxes" ? "md:col-span-2" : ""}>
            <SubField spec={spec} value={group[spec.suffix]} onChange={(v) => onChange({ ...group, [spec.suffix]: v })}
              testid={`${testid}-${spec.suffix}`} />
          </div>
        ))}
      </div>
    );
  } else if (q.type === "repeat") {
    body = <RepeatRows q={q} value={value} onChange={onChange} testid={testid} />;
  } else {
    body = <input type={q.type === "date" ? "date" : "text"} value={value || ""} onChange={(e) => onChange(e.target.value)}
      className={inputCls} data-testid={testid} autoFocus />;
  }
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.16em] text-stone-400">{q.section}</p>
      <h2 className="mt-2 font-heading text-2xl font-semibold leading-snug text-stone-900" data-testid={`${testid}-label`}>{q.label}</h2>
      {q.instruction && <p className="mt-2 rounded-xl bg-stone-50 px-4 py-3 text-sm leading-relaxed text-stone-600" data-testid={`${testid}-instruction`}>{q.instruction}</p>}
      <div className="mt-5">
        {body}
        {hasSubparts && (
          <div className="mt-4 grid gap-3 md:grid-cols-2" data-testid={`${testid}-subparts`}>
            {(q.subparts || []).map((spec) => (
              <div key={spec.suffix} className={spec.type === "textarea" || spec.type === "checkboxes" ? "md:col-span-2" : ""}>
                <SubField spec={spec} value={packed[spec.suffix]} onChange={(v) => onChange({ ...packed, [spec.suffix]: v })}
                  testid={`${testid}-${spec.suffix}`} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
